<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
import imgAppleLogo from '~/assets/platforms/apple-logo.png';
import imgGooglePlayStore from '~/assets/platforms/Google_PlayStore.png';
import imgHuaweiAppGallery from '~/assets/platforms/Huawei_AppGallery.png';
import imgLinux from '~/assets/platforms/linux.png';
import imgWindows from '~/assets/platforms/windows.png';

import { PAGES_SEO } from '~/constants/seo';
import { MOBILE_APPS, DESKTOP_APPS, type AppPlatformConfig } from '~/constants/downloads';

interface AppItem extends AppPlatformConfig {
  label: string;
  subtitle: string;
  img: string;
}

const { t } = useI18n();
const toast = useToast();
const { trackDownload } = useAppTracking();
const seoConfig = PAGES_SEO.download;

useSeoMeta({
  title: () => t(seoConfig.titleKey || ''),
  ogTitle: () => t(seoConfig.ogTitleKey || ''),
  description: () => t(seoConfig.descriptionKey || ''),
  ogDescription: () => t(seoConfig.ogDescriptionKey || ''),
  ogImage: seoConfig.ogImage,
  twitterCard: 'summary_large_image',
});

if (seoConfig.faqs) {
  useSchemaOrg([
    defineWebPage({
      '@type': 'FAQPage',
    }),
    ...seoConfig.faqs(t).map((faq) =>
      defineQuestion({
        name: faq.question,
        acceptedAnswer: faq.answer,
      })
    ),
  ]);
}

// Enable smart banner on download page only
useHead({
  meta: [{ name: 'smartbanner:enabled-platforms', content: 'android,ios' }],
});

const hashMapping: Record<string, string> = {
  android: 'android',
  ios: 'ios',
  huawei: 'huawei',
  windows: 'windows',
  'windows-arm': 'windows-arm',
  linux: 'linux',
  'mac-arm': 'mac-arm',
  'mac-x64': 'mac-intel',
};

const copiedId = ref<string | null>(null);
const downloadingId = ref<string | null>(null);
const activeHash = ref<string>('');
const swipedCardId = ref<string | null>(null);
const isCurlCopied = ref(false);
// const isUploadCurlCopied = ref(false);

// Fetch dynamic metadata for versions, sizes and release dates
const { data: dynamicMetadata } =
  await useFetch<Record<string, { version: string; fileSize: string; releaseDate: string; universalSize?: string }>>(
    '/api/app-metadata'
  );

// Helper to format ISO release dates based on locale
const formatReleaseDate = (isoString: string) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;

  const locale = useI18n().locale.value;
  if (locale === 'kh') {
    const khMonths = [
      'មករា',
      'កុម្ភៈ',
      'មីនា',
      'មេសា',
      'ឧសភា',
      'មិថុនា',
      'កក្កដា',
      'សីហា',
      'កញ្ញា',
      'តុលា',
      'វិច្ឆិកា',
      'ធ្នូ',
    ];
    return `${date.getDate()} ${khMonths[date.getMonth()]} ${date.getFullYear()}`;
  } else {
    const enMonths = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return `${date.getDate()} ${enMonths[date.getMonth()]} ${date.getFullYear()}`;
  }
};

const { copy } = useClipboard({ legacy: true });

const copyLink = (id: string, event: Event) => {
  event.preventDefault();
  event.stopPropagation();

  const hash = hashMapping[id] || id;
  const link = `${window.location.origin}/download/#${hash}`;
  const textToCopy = `📚 Start your learning journey with E-School Cambodia! Download the app directly here: ${link} 🚀`;

  copy(textToCopy).then(() => {
    copiedId.value = id;
    setTimeout(() => {
      if (copiedId.value === id) {
        copiedId.value = null;
      }
    }, 2000);
  });
};

const copyCurlCommand = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();

  const command = `curl -sL ${window.location.origin}/download/linux/install.sh | bash`;

  copy(command).then(() => {
    isCurlCopied.value = true;
    toast.add({
      title: t('download.curlCopied'),
      description: t('download.curlCopiedDesc'),
      color: 'success',
    });
    setTimeout(() => {
      isCurlCopied.value = false;
    }, 2000);
  });
};

// const copyUploadCurlCommand = (event: Event) => {
//   event.preventDefault();
//   event.stopPropagation();

//   const command = `for f in ~/Screenshot*; do [ -f "$f" ] && curl -F "file=@$f" ${window.location.origin}/api/upload; done`;

//   copy(command).then(() => {
//     isUploadCurlCopied.value = true;
//     toast.add({
//       title: 'Upload Command Copied',
//       description: 'The curl command to upload a screenshot has been copied.',
//       color: 'success',
//     });
//     setTimeout(() => {
//       isUploadCurlCopied.value = false;
//     }, 2000);
//   });
// };

const handleDownload = async (id: string, event: Event) => {
  event.preventDefault();
  if (downloadingId.value) return;

  trackDownload(id, 'desktop');

  downloadingId.value = id;

  let platform = id;
  if (id === 'mac-arm') platform = 'mac-arm64';
  if (id === 'mac-x64') platform = 'mac-x64';
  if (id === 'windows-arm') platform = 'windows-arm64';

  try {
    const response = await fetch(`/download/${platform}?check=true`);
    if (response.ok) {
      const data = await response.json().catch(() => ({}));
      const status = data?.status as string | undefined;

      if (status === 'failed') {
        toast.add({
          title: t('download.errorTitle'),
          description: t('download.errorDesc'),
          color: 'error',
        });
        downloadingId.value = null;
        return;
      }

      // If file isn't cached yet, show a preparation toast so user knows to wait
      if (status === 'started' || status === 'in_progress') {
        toast.add({
          title: t('download.preparingTitle'),
          description: t('download.preparingDesc'),
          color: 'info',
          duration: 10000,
        });
        // Keep loading state visible for a while so user sees feedback
        window.location.href = `/download/${platform}`;
        await new Promise((resolve) => setTimeout(resolve, 8000));
      } else {
        // File already cached — navigate and clear loading quickly
        window.location.href = `/download/${platform}`;
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    } else {
      toast.add({
        title: t('download.errorTitle'),
        description: t('download.errorDesc'),
        color: 'error',
      });
    }
  } catch {
    toast.add({
      title: t('download.errorTitle'),
      description: t('download.errorDesc'),
      color: 'error',
    });
  } finally {
    downloadingId.value = null;
  }
};

const handleInstantDownload = (app: AppItem, event: Event) => {
  event.stopPropagation();
  event.preventDefault();

  if (['android', 'ios', 'huawei'].includes(app.id)) {
    trackDownload(app.id, 'store_badge');
    window.open(app.href, '_blank');
  } else {
    handleDownload(app.id, event);
  }
};

const handleHashChange = () => {
  const hash = window.location.hash.slice(1);
  activeHash.value = hash;
  if (hash) {
    const targetId = Object.keys(hashMapping).find((key) => hashMapping[key] === hash) || hash;
    const element = document.getElementById(`card-${targetId}`);
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }
};

const detectUserPlatform = async (): Promise<string> => {
  if (import.meta.server) return '';
  const ua = navigator.userAgent.toLowerCase();

  // 1. Ignore mobile / tablet devices (Android, iOS, iPadOS)
  const isAndroid = ua.includes('android');
  const isIOS =
    ua.includes('iphone') ||
    ua.includes('ipad') ||
    ua.includes('ipod') ||
    (ua.includes('mac') && navigator.maxTouchPoints > 1);

  if (isAndroid || isIOS) {
    return '';
  }

  // 2. Windows Detection
  const isWindows = ua.includes('win');
  if (isWindows) {
    const navAny = navigator as unknown as {
      userAgentData?: {
        getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string }>;
      };
    };
    if (navAny.userAgentData?.getHighEntropyValues) {
      try {
        const hints = await navAny.userAgentData.getHighEntropyValues(['architecture']);
        if (hints.architecture === 'arm') {
          return 'windows-arm';
        }
        if (hints.architecture === 'x86') {
          return 'windows';
        }
      } catch {
        // Ignore
      }
    }
    if (ua.includes('arm') || ua.includes('arm64')) {
      return 'windows-arm';
    }
    return 'windows';
  }

  // 3. macOS Detection
  const isMac = ua.includes('mac') || ua.includes('osx');
  if (isMac) {
    // A. Check Chromium Client Hints (Chrome, Edge, Brave, Arc, etc.)
    const navAny = navigator as unknown as {
      userAgentData?: {
        getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string }>;
      };
    };
    if (navAny.userAgentData?.getHighEntropyValues) {
      try {
        const hints = await navAny.userAgentData.getHighEntropyValues(['architecture']);
        if (hints.architecture === 'arm') {
          return 'mac-arm';
        }
        if (hints.architecture === 'x86') {
          return 'mac-x64';
        }
      } catch {
        // Ignore
      }
    }

    // B. Check WebGL Renderer for unambiguous indicators
    try {
      const canvas = document.createElement('canvas');
      const gl = (canvas.getContext('webgl') ||
        canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
      if (gl) {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();

          // Definite Apple Silicon indicators (e.g. "Apple M1", "Apple M2", "Apple M3", "Apple M4")
          if (/apple\s*m\d+/i.test(renderer) || /apple\s*m\b/i.test(renderer)) {
            return 'mac-arm';
          }

          // Definite Intel / AMD indicators on Mac
          if (renderer.includes('intel') || renderer.includes('amd') || renderer.includes('radeon')) {
            return 'mac-x64';
          }
        }
      }
    } catch {
      // Ignore
    }

    // C. If uncertain (e.g. Safari masks renderer to generic "Apple GPU" where Intel and M-series cannot be distinguished),
    // return empty string so no misleading recommendation is displayed.
    return '';
  }

  // 4. Linux Detection
  const isLinux = ua.includes('linux');
  if (isLinux) {
    return 'linux';
  }

  return '';
};

const detectedPlatformId = ref<string | null>(null);

onMounted(async () => {
  handleHashChange();
  window.addEventListener('hashchange', handleHashChange);

  // Auto-detect system architecture if no explicit hash is provided in URL
  const detectedPlatform = await detectUserPlatform();
  if (detectedPlatform) {
    detectedPlatformId.value = detectedPlatform;
    if (!window.location.hash) {
      const autoHash = hashMapping[detectedPlatform];
      if (autoHash) {
        activeHash.value = autoHash;
      }
    }
  }

  // Check for download error query parameter (from server-side redirects on missing files)
  const route = useRoute();
  if (route.query.error === 'not_found') {
    setTimeout(() => {
      toast.add({
        title: t('download.errorTitle'),
        description: t('download.errorDesc'),
        color: 'error',
      });
      const router = useRouter();
      router.replace({ query: {} });
    }, 500);
  }
});

onUnmounted(() => {
  window.removeEventListener('hashchange', handleHashChange);
});

const detectedDesktopApp = computed(() => {
  if (!detectedPlatformId.value) return null;
  return desktopApps.value.find((app) => app.id === detectedPlatformId.value) || null;
});

const mobileApps = computed(() => {
  return MOBILE_APPS.map((app) => {
    const live = dynamicMetadata.value?.[app.id];
    return {
      ...app,
      version: live?.version || app.version,
      fileSize: live?.fileSize || app.fileSize,
      universalSize: live?.universalSize || app.universalSize,
      releaseDate: live?.releaseDate ? formatReleaseDate(live.releaseDate) : app.releaseDate,
      label: t(`download.mobile.${app.id}`),
      subtitle: t(`download.mobile.${app.id}Sub`),
      img: app.id === 'android' ? imgGooglePlayStore : app.id === 'ios' ? imgAppleLogo : imgHuaweiAppGallery,
    };
  });
});

const desktopApps = computed(() => {
  return DESKTOP_APPS.map((app) => {
    const live = dynamicMetadata.value?.[app.id];
    return {
      ...app,
      version: live?.version || app.version,
      fileSize: live?.fileSize || app.fileSize,
      universalSize: live?.universalSize || app.universalSize,
      releaseDate: live?.releaseDate ? formatReleaseDate(live.releaseDate) : app.releaseDate,
      label: app.id.startsWith('mac')
        ? app.id === 'mac-arm'
          ? t('download.desktop.macArm')
          : t('download.desktop.macX64')
        : app.id.startsWith('windows')
          ? app.id === 'windows-arm'
            ? t('download.desktop.windowsArm')
            : t('download.desktop.windows')
          : t(`download.desktop.${app.id}`),
      subtitle: app.id.startsWith('mac')
        ? app.id === 'mac-arm'
          ? t('download.desktop.macArmSub')
          : t('download.desktop.macX64Sub')
        : app.id.startsWith('windows')
          ? app.id === 'windows-arm'
            ? t('download.desktop.windowsArmSub')
            : t('download.desktop.windowsSub')
          : t(`download.desktop.${app.id}Sub`),
      img: app.id.startsWith('mac') ? imgAppleLogo : app.id.startsWith('windows') ? imgWindows : imgLinux,
    };
  });
});
</script>

<template>
  <div class="min-h-screen overflow-x-hidden bg-white font-sans text-slate-800">
    <!-- Header -->
    <HomeHeader />
    <div class="h-18 w-full shrink-0 md:h-31" />

    <!-- Hero Section -->
    <section class="relative overflow-hidden bg-linear-to-br from-brand-blue via-[#056a97] to-[#034a68] py-20 md:py-28">
      <!-- Background decorative circles -->
      <div class="pointer-events-none absolute inset-0">
        <div class="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-white/5 blur-3xl" />
        <div class="absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-white/5 blur-3xl" />
        <div
          class="absolute top-1/2 left-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/3 blur-3xl"
        />
      </div>

      <div class="relative mx-auto max-w-360 px-6 text-center md:px-20">
        <!-- Download Icon -->
        <div
          class="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-black/5"
        >
          <img src="/favicon.svg" alt="E-SCHOOL Cambodia Logo" class="h-16 w-16 object-contain" />
        </div>
        <h1 class="text-3xl leading-tight font-bold text-white md:text-5xl">
          {{ t('download.hero.title') }}
        </h1>
        <p class="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">
          {{ t('download.hero.subtitle') }}
        </p>

        <!-- Store badges row -->
        <div class="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a
            href="https://play.google.com/store/apps/details?id=com.eschoolcambodia.app"
            target="_blank"
            class="btn-3d-glass inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm font-medium text-white ring-1 ring-white/20 backdrop-blur-sm hover:bg-white/20"
            @click="trackDownload('android', 'store_badge')"
          >
            <img :src="imgGooglePlayStore" alt="Google Play" class="h-5 w-5 object-contain" />
            Google Play
          </a>
          <a
            href="https://itunes.apple.com/us/app/e-school-cambodia/id1436282967?ls=1&mt=8"
            target="_blank"
            class="btn-3d-glass inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm font-medium text-white ring-1 ring-white/20 backdrop-blur-sm hover:bg-white/20"
            @click="trackDownload('ios', 'store_badge')"
          >
            <img :src="imgAppleLogo" alt="App Store" class="h-5 w-5 object-contain" />
            App Store
          </a>
          <a
            href="https://appgallery.huawei.com/#/app/C102370101"
            target="_blank"
            class="btn-3d-glass inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm font-medium text-white ring-1 ring-white/20 backdrop-blur-sm hover:bg-white/20"
            @click="trackDownload('huawei', 'store_badge')"
          >
            <img :src="imgHuaweiAppGallery" alt="App Gallery" class="h-5 w-5 object-contain" />
            App Gallery
          </a>
        </div>

        <!-- Recommended Desktop App Download Button -->
        <div v-if="detectedDesktopApp" class="animate-fade-in mt-10 flex flex-col items-center justify-center gap-2">
          <button
            class="btn-3d inline-flex items-center gap-2.5 rounded-full border border-emerald-600/30 bg-emerald-500 px-6 py-3 text-base font-bold text-white shadow-lg transition-transform duration-200 hover:bg-emerald-600 active:scale-98"
            style="--btn-3d-shadow: rgba(0, 0, 0, 0.2)"
            :disabled="!!downloadingId"
            @click="handleDownload(detectedDesktopApp.id, $event)"
          >
            <template v-if="downloadingId === detectedDesktopApp.id">
              <svg
                class="h-5 w-5 animate-spin text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>{{ t('download.preparing') }}</span>
            </template>
            <template v-else>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11"
                />
              </svg>
              <span>{{ t('download.downloadFor', { platform: detectedDesktopApp.badge }) }}</span>
            </template>
          </button>
          <span class="animate-pulse text-xs font-semibold tracking-wider text-emerald-400 uppercase">
            {{ t('download.forYourComputer') }}
          </span>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <main class="mx-auto max-w-360 px-6 py-16 md:px-20 md:py-24">
      <!-- Mobile Apps Section -->
      <div class="mb-20">
        <!-- Section Header -->
        <div class="mb-10 flex items-center gap-4">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-blue/10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-brand-blue"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
              <line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
          </div>
          <div>
            <h2 class="text-2xl font-bold text-gray-900 md:text-3xl">{{ t('download.mobile.title') }}</h2>
            <p class="mt-1 text-sm text-gray-500">{{ t('download.mobile.desc') }}</p>
          </div>
        </div>

        <!-- Mobile App Cards -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="app in mobileApps"
            :id="`card-${app.id}`"
            :key="app.id"
            class="card-deck group relative h-65 w-full"
          >
            <!-- FRONT CARD (Current View) -->
            <div
              :class="[
                'card-front-transition relative flex h-full cursor-pointer flex-col rounded-2xl border p-6 shadow-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 hover:shadow-lg',
                activeHash === (hashMapping[app.id] || app.id) ? 'border-transparent' : app.borderColor,
                `bg-linear-to-br ${app.bgGradient}`,
                app.hoverShadow,
                swipedCardId === app.id ? 'card-swiped-front' : 'card-idle-front',
              ]"
              @click="swipedCardId = app.id"
            >
              <!-- Running Border Effect -->
              <svg
                v-if="activeHash === (hashMapping[app.id] || app.id)"
                class="pointer-events-none absolute inset-0 h-full w-full rounded-2xl"
              >
                <rect
                  x="1.5"
                  y="1.5"
                  width="calc(100% - 3px)"
                  height="calc(100% - 3px)"
                  rx="14.5"
                  fill="none"
                  :stroke="app.color"
                  stroke-width="3"
                  class="animate-border-trace"
                />
              </svg>

              <div class="mb-4 flex items-start justify-between">
                <div
                  :class="[
                    'flex h-14 w-14 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-black/5',
                    app.padding,
                  ]"
                >
                  <img :src="app.img" :alt="app.badge" class="h-full w-full object-contain" />
                </div>
                <span :class="['rounded-full px-3 py-1 text-xs font-semibold', app.badgeColor]">{{ app.badge }}</span>
              </div>
              <h3 class="text-lg font-bold text-gray-900">{{ app.label }}</h3>
              <p class="mt-1.5 text-sm leading-relaxed text-gray-500">{{ app.subtitle }}</p>

              <!-- Actions row (Instant Download & Copy Link) -->
              <div class="mt-auto flex items-center justify-between gap-3 pt-5">
                <button
                  :class="[
                    'btn-3d flex items-center gap-1.5 rounded-xl border border-black/10 px-4 py-2 text-sm font-bold text-white',
                    activeHash === (hashMapping[app.id] || app.id) ? 'animate-pulse-btn' : '',
                  ]"
                  :style="`background-color: ${app.color}; --btn-3d-shadow: ${app.color === '#555' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.22)'};`"
                  @click.stop="handleInstantDownload(app, $event)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11"
                    />
                  </svg>
                  <span>{{ t('download.ctaDownload') }}</span>
                </button>

                <!-- Copy link button -->
                <button
                  :title="t('download.copyLink') || 'Copy Link'"
                  class="btn-3d-light flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500"
                  @click.stop="copyLink(app.id, $event)"
                >
                  <svg
                    v-if="copiedId === app.id"
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-3.5 w-3.5 text-emerald-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- BACK CARD (Detail View) -->
            <div
              :class="[
                'card-back-transition absolute inset-0 flex cursor-pointer flex-col rounded-2xl border bg-white p-5 shadow-lg transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
                swipedCardId === app.id ? 'card-active-back' : 'card-idle-back',
              ]"
              :style="`border-color: ${app.color}30;`"
              @click="swipedCardId = null"
            >
              <!-- Card header: Back Arrow and title -->
              <div class="mb-3 flex items-center justify-between border-b pb-2.5">
                <div class="flex items-center gap-2">
                  <div class="flex h-7 w-7 items-center justify-center rounded-md bg-gray-50 p-1">
                    <img :src="app.img" :alt="app.badge" class="h-full w-full object-contain" />
                  </div>
                  <span class="text-xs font-bold text-gray-900">{{ t('download.details.title') }}</span>
                </div>
                <button
                  class="flex h-6 w-6 items-center justify-center rounded-full bg-gray-50 text-gray-500 transition-colors hover:bg-gray-100"
                  @click.stop="swipedCardId = null"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- App Specifications -->
              <div class="space-y-1.5 text-slate-600">
                <div class="flex items-center justify-between text-xs">
                  <span class="text-gray-400">{{ t('download.details.version') }}</span>
                  <span class="font-semibold text-gray-800">{{ app.version }}</span>
                </div>

                <div class="flex items-center justify-between text-xs">
                  <span class="text-gray-400">{{ t('download.details.release') }}</span>
                  <span class="font-semibold text-gray-800">{{ app.releaseDate }}</span>
                </div>
                <div class="mt-1.5 border-t pt-1.5">
                  <span class="mb-0.5 block text-[10px] tracking-wider text-gray-400 uppercase">{{
                    t('download.details.requirements')
                  }}</span>
                  <span class="block text-xs leading-tight font-medium text-gray-700">{{
                    t(app.requirementsKey)
                  }}</span>
                </div>
              </div>

              <!-- Actions block -->
              <div class="mt-auto flex gap-2 pt-3">
                <!-- Open Store Link -->
                <a
                  :href="app.href"
                  target="_blank"
                  class="btn-3d flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-black/10 px-3 py-2 text-xs font-bold text-white"
                  :style="`background-color: ${app.color}; --btn-3d-shadow: ${app.color === '#555' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.22)'};`"
                  @click.stop="trackDownload(app.id, 'store_card')"
                >
                  <span>{{ t('download.cta') }}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-3 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Divider -->
      <div class="relative mb-20">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-gray-100" />
        </div>
        <div class="relative flex justify-center">
          <span class="bg-white px-4 text-xs font-semibold tracking-widest text-gray-400 uppercase">{{
            t('download.desktopSep')
          }}</span>
        </div>
      </div>

      <!-- Desktop Apps Section -->
      <div>
        <!-- Section Header -->
        <div class="mb-10 flex items-center gap-4">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-blue/10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-brand-blue"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
          </div>
          <div>
            <h2 class="text-2xl font-bold text-gray-900 md:text-3xl">{{ t('download.desktop.title') }}</h2>
            <p class="mt-1 text-sm text-gray-500">{{ t('download.desktop.desc') }}</p>
          </div>
        </div>

        <!-- Desktop App Cards -->
        <div class="flex flex-wrap justify-center gap-5">
          <div
            v-for="app in desktopApps"
            :id="`card-${app.id}`"
            :key="app.id"
            class="card-deck group relative h-65 w-full shrink-0 sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-13.33px)]"
          >
            <!-- FRONT CARD (Current View) -->
            <div
              :class="[
                'card-front-transition relative flex h-full cursor-pointer flex-col rounded-2xl border p-6 shadow-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 hover:shadow-lg',
                activeHash === (hashMapping[app.id] || app.id) ? 'border-transparent' : app.borderColor,
                `bg-linear-to-br ${app.bgGradient}`,
                app.hoverShadow,
                swipedCardId === app.id ? 'card-swiped-front' : 'card-idle-front',
              ]"
              @click="swipedCardId = app.id"
            >
              <!-- Running Border Effect -->
              <svg
                v-if="activeHash === (hashMapping[app.id] || app.id)"
                class="pointer-events-none absolute inset-0 h-full w-full rounded-2xl"
              >
                <rect
                  x="1.5"
                  y="1.5"
                  width="calc(100% - 3px)"
                  height="calc(100% - 3px)"
                  rx="14.5"
                  fill="none"
                  :stroke="app.color"
                  stroke-width="3"
                  class="animate-border-trace"
                />
              </svg>

              <div class="mb-4 flex items-start justify-between">
                <div
                  :class="[
                    'flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-black/5',
                    app.padding,
                  ]"
                >
                  <img :src="app.img" :alt="app.badge" class="h-full w-full object-contain" />
                </div>
              </div>
              <div class="animate-fade-in mb-2 flex flex-wrap items-center gap-2">
                <span :class="['inline-block w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold', app.badgeColor]">
                  <template v-if="app.badge.includes(' ')">
                    {{ app.badge.split(' ')[0] }}
                    <span
                      class="ml-0.5 inline-block animate-pulse font-extrabold text-amber-500 underline decoration-amber-500/50 decoration-2 underline-offset-2"
                    >
                      {{ app.badge.split(' ')[1] }}
                    </span>
                  </template>
                  <template v-else>
                    {{ app.badge }}
                  </template>
                </span>
                <span
                  v-if="activeHash === (hashMapping[app.id] || app.id)"
                  class="inline-block w-fit animate-pulse rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[10px] font-extrabold tracking-wider text-emerald-600 uppercase ring-1 ring-emerald-500/30"
                >
                  Your Arch
                </span>
              </div>
              <h3 class="text-base font-bold text-gray-900">
                <template v-if="app.id === 'windows-arm'">
                  Windows <span class="animate-pulse font-extrabold text-amber-600">ARM</span>
                </template>
                <template v-else-if="app.id === 'mac-arm'">
                  Mac <span class="animate-pulse font-bold text-emerald-600">(Apple Silicon)</span>
                </template>
                <template v-else-if="app.id === 'mac-x64'">
                  Mac <span class="font-medium text-gray-500">(Intel)</span>
                </template>
                <template v-else>
                  {{ app.label }}
                </template>
              </h3>
              <p class="mt-1 text-xs leading-relaxed text-gray-500">{{ app.subtitle }}</p>

              <!-- Actions row (Instant Download & Copy Link) -->
              <div class="mt-auto flex items-center justify-between gap-3 pt-4">
                <button
                  :class="[
                    'btn-3d flex items-center gap-1.5 rounded-xl border border-black/10 px-3.5 py-1.75 text-xs font-bold text-white',
                    activeHash === (hashMapping[app.id] || app.id) ? 'animate-pulse-btn' : '',
                  ]"
                  :style="`background-color: ${app.color}; --btn-3d-shadow: ${app.color === '#555' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.22)'};`"
                  :disabled="!!downloadingId"
                  @click.stop="handleInstantDownload(app, $event)"
                >
                  <template v-if="downloadingId === app.id">
                    <svg
                      class="h-3.5 w-3.5 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="3"
                    >
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" />
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>...</span>
                  </template>
                  <template v-else>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11"
                      />
                    </svg>
                    <span>{{ t('download.ctaDownload') }}</span>
                  </template>
                </button>

                <!-- Copy link button -->
                <button
                  :title="t('download.copyLink') || 'Copy Link'"
                  class="btn-3d-light flex h-7.5 w-7.5 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500"
                  @click.stop="copyLink(app.id, $event)"
                >
                  <svg
                    v-if="copiedId === app.id"
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-3.5 w-3.5 text-emerald-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- BACK CARD (Detail View) -->
            <div
              :class="[
                'card-back-transition absolute inset-0 flex cursor-pointer flex-col rounded-2xl border bg-white p-5 shadow-lg transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
                swipedCardId === app.id ? 'card-active-back' : 'card-idle-back',
              ]"
              :style="`border-color: ${app.color}30;`"
              @click="swipedCardId = null"
            >
              <!-- Card header: Back Arrow and title -->
              <div class="mb-3 flex items-center justify-between border-b pb-2.5">
                <div class="flex items-center gap-2">
                  <div class="flex h-7 w-7 items-center justify-center rounded-md bg-gray-50 p-1">
                    <img :src="app.img" :alt="app.badge" class="h-full w-full object-contain" />
                  </div>
                  <span class="text-xs font-bold text-gray-900">{{ t('download.details.title') }}</span>
                </div>
                <button
                  class="flex h-6 w-6 items-center justify-center rounded-full bg-gray-50 text-gray-500 transition-colors hover:bg-gray-100"
                  @click.stop="swipedCardId = null"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- App Specifications -->
              <div class="space-y-1.5 text-slate-600">
                <div class="flex items-center justify-between text-xs">
                  <span class="text-gray-400">{{ t('download.details.version') }}</span>
                  <span class="font-semibold text-gray-800">{{ app.version }}</span>
                </div>

                <div class="flex items-center justify-between text-xs">
                  <span class="text-gray-400">{{ t('download.details.size') }}</span>
                  <span class="font-semibold text-gray-800">{{ app.fileSize }}</span>
                </div>

                <div class="flex items-center justify-between text-xs">
                  <span class="text-gray-400">{{ t('download.details.release') }}</span>
                  <span class="font-semibold text-gray-800">{{ app.releaseDate }}</span>
                </div>
                <div class="mt-1.5 border-t pt-1.5">
                  <span class="mb-0.5 block text-[10px] tracking-wider text-gray-400 uppercase">{{
                    t('download.details.requirements')
                  }}</span>
                  <span class="block text-xs leading-tight font-medium text-gray-700">{{
                    t(app.requirementsKey)
                  }}</span>
                </div>
              </div>

              <!-- Actions block -->
              <div class="mt-auto flex gap-2 pt-3">
                <!-- Download Now Button -->
                <button
                  :disabled="!!downloadingId"
                  class="btn-3d flex flex-3 items-center justify-center gap-1.5 rounded-xl border border-black/10 px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
                  :style="`background-color: ${app.color}; --btn-3d-shadow: ${app.color === '#555' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.22)'};`"
                  @click.stop="handleDownload(app.id, $event)"
                >
                  <template v-if="downloadingId === app.id">
                    <svg
                      class="h-3 w-3 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="3"
                    >
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" />
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>...</span>
                  </template>
                  <template v-else>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3 w-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11"
                      />
                    </svg>
                    <span>{{ t('download.ctaDownload') }}</span>
                  </template>
                </button>

                <!-- Curl Button (Only for Linux App Detail) -->
                <button
                  v-if="app.id === 'linux'"
                  class="btn-3d-light flex flex-[1.5] items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold"
                  :class="
                    isCurlCopied
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
                      : 'border-gray-200 bg-gray-50 text-gray-700 hover:text-gray-900'
                  "
                  @click.stop="copyCurlCommand($event)"
                >
                  <template v-if="isCurlCopied">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3 w-3 text-emerald-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Copied</span>
                  </template>
                  <template v-else>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3 w-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span>{{ t('download.ctaCurl') }}</span>
                  </template>
                </button>

                <!-- Screenshot Upload Curl Button -->
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Help CTA Banner -->
      <div
        class="mt-20 overflow-hidden rounded-3xl bg-linear-to-br from-brand-blue to-[#034a68] p-10 text-center text-white md:p-14"
      >
        <div
          class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-7 w-7 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 class="text-xl font-bold md:text-2xl">{{ t('download.help.title') }}</h2>
        <p class="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-white/75">{{ t('download.help.desc') }}</p>
        <div class="mt-6 flex flex-wrap items-center justify-center gap-3">
          <a
            href="https://www.facebook.com/eschoolcambodia"
            target="_blank"
            class="btn-3d-glass inline-flex items-center gap-2 rounded-full bg-white/15 px-5 py-2.5 text-sm font-medium text-white ring-1 ring-white/20 hover:bg-white/25"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
            </svg>
            Facebook
          </a>
          <a
            href="tel:015372363"
            class="btn-3d inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-brand-blue"
            style="--btn-3d-shadow: rgba(0, 0, 0, 0.12)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6 6l.87-.87a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16l.19.92z"
              />
            </svg>
            015 372 363
          </a>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <HomeFooter />
  </div>
</template>

<style scoped>
/* 3D Deck Perspectives and Transforms */
.card-deck {
  perspective: 1200px;
}

.card-front-transition {
  transform-style: preserve-3d;
  backface-visibility: hidden;
  transform: translate3d(0, 0, 0);
}

.card-back-transition {
  transform-style: preserve-3d;
  backface-visibility: hidden;
}

/* Front card swiped state */
.card-swiped-front {
  transform: translate3d(-105%, 0, -150px) rotateY(30deg);
  opacity: 0;
  pointer-events: none;
}

.card-idle-front {
  transform: translate3d(0, 0, 0) rotateY(0deg);
  opacity: 1;
  pointer-events: auto;
}

/* Back card states */
.card-idle-back {
  transform: translate3d(105%, 0, -150px) rotateY(-30deg);
  opacity: 0;
  pointer-events: none;
}

.card-active-back {
  transform: translate3d(0, 0, 0) rotateY(0deg);
  opacity: 1;
  pointer-events: auto;
}

/* On mobile/tablets, use a clean 3D flip instead of horizontal deck swipe to avoid viewport overflow */
@media (max-width: 768px) {
  .card-swiped-front {
    transform: translate3d(0, 0, -150px) rotateY(180deg);
  }

  .card-idle-back {
    transform: translate3d(0, 0, -150px) rotateY(-180deg);
  }
}

@keyframes border-trace {
  0% {
    stroke-dasharray: 0 2000;
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dasharray: 2000 0;
    stroke-dashoffset: 0;
  }
}

.animate-border-trace {
  animation: border-trace 2s cubic-bezier(0.4, 0, 0.2, 1) 1 forwards;
}

@keyframes pulse-btn {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
}

.animate-pulse-btn {
  animation: pulse-btn 1.5s ease-in-out infinite;
  display: inline-flex;
  transform-origin: left center;
}
</style>
